import { TransactionType } from '../../application/types/transactionType';
import { Alarm } from '@mui/icons-material';
import { Alert, AlertTitle } from '@mui/material';

/**
 * Helper function to check if required fields are missing based on transaction type.
 */
const isTransactionFieldsMissing = (
    transactionType: TransactionType,
    selectedDrug: any,
    selectedStorage: any,
    quantityChanged: any,
    storageRemaining: any,
    selectedEmergency?: any,
    emergencyRemaining?: any
): boolean => {
    switch (transactionType) {
        case TransactionType.CheckOut:
        case TransactionType.CheckIn:
            return (
                !selectedDrug ||
                !selectedStorage ||
                !quantityChanged ||
                !storageRemaining
            );

        case TransactionType.AmbulanceTransaction:
            return (
                !selectedDrug ||
                !selectedStorage ||
                !quantityChanged ||
                !storageRemaining ||
                !selectedEmergency ||
                !emergencyRemaining
            );

        case TransactionType.ShiftCrosscheck:
            return !selectedEmergency;

        case TransactionType.Crosscheck:
            return !selectedStorage;
        case TransactionType.ConnectedTransactionCheckOut:
            return false;
        case TransactionType.ConnectedTransactionCheckIn:
            return false;

        default:
            return false;
    }
};

/**
 * Determines whether the Sign button should be disabled.
 */
export const isSignButtonDisabled = (
    transactionType: TransactionType,
    selectedDrug: any,
    selectedStorage: any,
    quantityChanged: any,
    storageRemaining: any,
    selectedEmergency?: any,
    emergencyRemaining?: any
): boolean => {
    return isTransactionFieldsMissing(
        transactionType,
        selectedDrug,
        selectedStorage,
        quantityChanged,
        storageRemaining,
        selectedEmergency,
        emergencyRemaining
    );
};

/**
 * Generates an alert if required fields are missing.
 */
export const getTransactionAlert = (
    transactionType: TransactionType,
    selectedDrug: any,
    selectedStorage: any,
    quantityChanged: any,
    storageRemaining: any,
    selectedEmergency?: any,
    emergencyRemaining?: any
) => {
    if (
        !isTransactionFieldsMissing(
            transactionType,
            selectedDrug,
            selectedStorage,
            quantityChanged,
            storageRemaining,
            selectedEmergency,
            emergencyRemaining
        )
    ) {
        return null;
    }

    const alertMessages: Record<TransactionType, string> = {
        [TransactionType.CheckOut]:
            'Lagerställe och läkemedel måste anges. Gå tillbaka och uppdatera de stegen.',
        [TransactionType.CheckIn]:
            'Lagerställe och läkemedel måste anges. Gå tillbaka och uppdatera de stegen.',
        [TransactionType.AmbulanceTransaction]:
            'Läkemedel, Läkemedelsskåp och Akutväska är obligatoriska. Gå tillbaka och uppdatera de stegen.',
        [TransactionType.ShiftCrosscheck]:
            'Lagerställe måste anges. Gå tillbaka och uppdatera de stegen.',
        [TransactionType.Crosscheck]:
            'Lagerställe måste anges. Gå tillbaka och uppdatera de stegen.',
        [TransactionType.ConnectedTransactionCheckOut]: '',
        [TransactionType.ConnectedTransactionCheckIn]: '',
        [TransactionType.ConnectedTransactionDepositToAmbulanceBag]: '',
        [TransactionType.ConnectedTransactionDispose]: '',
        [TransactionType.Disposed]:
            'Lagerställe och läkemedel måste anges. Gå tillbaka och uppdatera de stegen.',
        [TransactionType.OperatingRoomTransfer]: '',
        [TransactionType.OperatingRoomReturn]: '',
        [TransactionType.ConsumptionInOperatingRoom]: '',
    };

    return (
        <Alert
            severity="warning"
            icon={<Alarm fontSize="inherit" />}
            variant="filled"
            sx={{ p: 2, borderRadius: 2, maxWidth: 400 }}
        >
            <AlertTitle>Information saknas</AlertTitle>
            {alertMessages[transactionType]}
        </Alert>
    );
};
